import { useCallback, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  closeNewProject,
  closeColumnSettings,
  closeNoPermission,
  setManageColumns,
} from 'app/slices/dashboardSlice';
import { useColumns, useCurrentUser } from 'hooks/useDashboardData';
import { dashboardApi } from 'app/services/dashboardApi';
import NewProjectModal from './NewProjectModal';
import ColumnSettingsModal from './ColumnSettingsModal';
import NoPermissionDialog from './NoPermissionDialog';

const { useCreateProjectMutation, useUpdateUserMutation } = dashboardApi;

/**
 * Container for the dashboard's stand-alone modals (Part B), wired to the real
 * dashboardApi:
 *  - NewProjectModal → portaled into `#new_project_popup` (the Backbone mount),
 *    Create → POST /projects/ (parity 83-89f).
 *  - ColumnSettingsModal → portaled into `#settings` (the Backbone mount),
 *    Done → PATCH user.manage_columns + rebuild (parity 52-58).
 *  - NoPermissionDialog → portaled into <body> (the Backbone "append to body"),
 *    shown when opening a no-access project (parity 76).
 *
 * SortConfirm + DeleteProjectConfirm are owned by ProjectTable (Part A), since
 * they are driven by per-row / per-column state already local to the table.
 *
 * Each modal renders only while its dashboardSlice flag is set, so the
 * focus/select + default-name effects re-run fresh on every open (re-mount =
 * Backbone "re-instantiated each open").
 *
 * The `#new_project_popup` / `#settings` mount nodes are rendered by
 * DashboardShell. We resolve them once in a layout effect (after the shell has
 * committed) so the portals always have a target.
 */
const DashboardModals = () => {
  const dispatch = useAppDispatch();
  const { data: columns } = useColumns();
  const { data: user } = useCurrentUser();
  const [createProject] = useCreateProjectMutation();
  const [updateUser] = useUpdateUserMutation();

  const newProjectOpen = useAppSelector((s) => s.dashboard.modals.newProject);
  const columnSettingsOpen = useAppSelector((s) => s.dashboard.modals.columnSettings);
  const noPermissionOpen = useAppSelector((s) => s.dashboard.modals.noPermission);
  const manageColumns = useAppSelector((s) => s.dashboard.manageColumns);

  // Resolve the canonical Backbone mount nodes after the shell has committed.
  const [mounts, setMounts] = useState<{
    newProject: HTMLElement | null;
    settings: HTMLElement | null;
  }>({ newProject: null, settings: null });
  useLayoutEffect(() => {
    setMounts({
      newProject: document.getElementById('new_project_popup'),
      settings: document.getElementById('settings'),
    });
  }, []);

  // ---- New Project ----
  const onCreate = useCallback(
    (name: string) => {
      // ProjectsView.add_new_project shows the process preloader immediately,
      // before the POST resolves (parity 178 / §3.E ADD .active place 1).
      document.getElementById('process_dotted_preloader')?.classList.add('active');
      createProject({ name });
    },
    [createProject]
  );
  const onCloseNewProject = useCallback(() => dispatch(closeNewProject()), [dispatch]);

  // ---- Manage Columns ----
  // Done → persist the ordered visible ids to user.manage_columns (CSV) +
  // rebuild the table (slice manageColumns drives orderedVisibleColumns).
  const onDoneColumns = useCallback(
    (orderedIds: number[]) => {
      dispatch(setManageColumns(orderedIds));
      if (user?.id != null) {
        updateUser({ id: user.id, body: { manage_columns: orderedIds.join(',') } });
      }
      dispatch(closeColumnSettings());
    },
    [dispatch, user?.id, updateUser]
  );
  const onCancelColumns = useCallback(() => dispatch(closeColumnSettings()), [dispatch]);

  // ---- No Permission ----
  const onOkNoPermission = useCallback(() => dispatch(closeNoPermission()), [dispatch]);

  return (
    <>
      {newProjectOpen &&
        mounts.newProject &&
        createPortal(
          <NewProjectModal onCreate={onCreate} onClose={onCloseNewProject} />,
          mounts.newProject
        )}

      {columnSettingsOpen &&
        mounts.settings &&
        createPortal(
          <ColumnSettingsModal
            columns={columns}
            manageColumns={manageColumns}
            onDone={onDoneColumns}
            onCancel={onCancelColumns}
          />,
          mounts.settings
        )}

      {noPermissionOpen &&
        typeof document !== 'undefined' &&
        createPortal(<NoPermissionDialog onOk={onOkNoPermission} />, document.body)}
    </>
  );
};

export default DashboardModals;
